import React, { useState, useEffect } from 'react';
import { Card, Button, Offcanvas, Form } from 'react-bootstrap';
import './ReservationStack.scss';
import {
  FaCheck,
  FaBan,
  FaTimes,
  FaUserFriends,
  FaPhone,
  FaFilter
} from 'react-icons/fa';
import {
  RenderCancelModal,
  ConfirmReservationModal,
  ApproveReservationModal,
  RejectReservationModal,
  MarkArrrivedModal,
  MarkAttendedModal
} from '../RenderFunctions';
import { getTodayReservationsByLocalIdAndDateRange, getReservationsFromCurrentSessionId } from '../../../../../../crud/clients_api/client_reservations';
import { useNotifications } from '../../../../../Base/components/NavBar/components/HeaderProfile/components/NotificationCenter/NotificationContext';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import DateFilter from '../../../../Caja/Receipts/components/Filters/DateFilter';
import { LocalSession } from '../../../../../../types/LocalSession';
import { toast } from 'react-toastify';


interface Reservation {
  reservation_id: number;
  client_id: number | null; // Allow null for compatibility with ReservationModal
  client_name: string;
  email: string;
  phone_number: string;
  reservation_date: string; // Date in 'YYYY-MM-DD' format
  local_id: number;
  init: number; // Start time as a slot (e.g., 18 for 6:00 PM)
  end: number; // End time as a slot (e.g., 20 for 8:00 PM)
  n_people: number; // Number of people for the reservation
  mesa_id: number; // Table ID
  mesa_num: number; // Table number
  description: string; // Description of the reservation
  confirmed: boolean; // Whether the reservation is confirmed
  approved: boolean; // Whether the reservation is approved
  attended: boolean; // Whether the reservation was attended
  cancelled: boolean; // Whether the reservation was cancelled
  arrived: boolean; // Whether the reservation has arrived
}


interface ReservationStackProps {
  data?: Reservation[];
  lastSession?: LocalSession | null;
}

const ReservationStack: React.FC<ReservationStackProps> = ({ data, lastSession }) => {
  const { reservations, setReservations } = useNotifications();
  const [filter, setFilter] = useState<'Canceladas'|'En proceso'|'Confirmadas'|'Por Confirmar'>('Por Confirmar');
  const [currentModal, setCurrentModal] = useState<'cancel'|'confirm'|'approved'|'arrived'|'attended'|'reject'|null>(null);
  const [selectedReservation, setSelectedReservation] = useState<Reservation|null>(null);
  const [loading, setLoading] = useState(true);
  const [soloSesionActual, setSoloSesionActual] = useState(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);


  const today = new Date().toISOString().split('T')[0];
  // Función para sumar días
  const addDays = (dateStr: string, days: number) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(addDays(today, 6));

useEffect(() => {
  if (soloSesionActual) {
    let start = '';
    if (lastSession && lastSession.session_start) {
      start = lastSession.session_start.split('T')[0];
    } else {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      start = `${yyyy}-${mm}-${dd}`;
    }
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    setStartDate(start);
    setEndDate(todayStr);
  }
}, [soloSesionActual, lastSession]);


  useEffect(() => {
    setLoading(true);
    const user = JSON.parse(localStorage.getItem('user')||'{}');
    const localId = user.local_id || 0;
    let response;
    if (soloSesionActual) {
      response = getReservationsFromCurrentSessionId(localId);
    } else {
      response = getTodayReservationsByLocalIdAndDateRange(localId, startDate, endDate);
    }
    response
      .then(r => {
        setReservations(r.data);
        setLoading(false);
      })
      .catch(() => {
        toast.error('Error obteniendo reservaciones');
      }).finally
      (() => setLoading(false))
      ;
  }
  , [soloSesionActual, startDate, endDate]);


  const timeFromInit = (init: number) => {
    const h = Math.floor(init/4), m = (init%4)*15;
    return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`;
  };

  const handleCancelReservation = (reservationId: number) => {
    setReservations(prevReservations =>
      prevReservations.map(r =>
        r.reservation_id === reservationId ? { ...r, cancelled: true } : r
      )
    );
  };

  const handleConfirmReservation = (reservationId: number) => {
    setReservations(prevReservations =>
      prevReservations.map(r =>
        r.reservation_id === reservationId ? { ...r, confirmed: true } : r
      )
    );
  };

  const handleRejectReservation = (reservationId: number) => {
    setReservations(prevReservations =>
      prevReservations.map(r =>
        r.reservation_id === reservationId ? { ...r, cancelled: true } : r
      )
    );
  };

  const handleMarkAsArrived = (reservationId: number) => {
    setReservations(prevReservations =>
      prevReservations.map(r =>
        r.reservation_id === reservationId ? { ...r, arrived: true } : r
      )
    );
  };

  const handleMarkAsAttended = (reservationId: number) => {
    setReservations(prevReservations =>
      prevReservations.map(r =>
        r.reservation_id === reservationId ? { ...r, attended: true, arrived: true } : r
      )
    );
  }

  const select = (res: Reservation, mode: typeof currentModal) => {
    setSelectedReservation(res);
    setCurrentModal(mode);
  };

  const filtered = reservations
  .filter(r => {
    // Filtrado por fecha (sesión actual o rango)
    let fechaValida = true;
    if (soloSesionActual && lastSession && lastSession.session_start && lastSession.session_end) {
      const start = new Date(lastSession.session_start).getTime();
      const end = new Date(lastSession.session_end).getTime();
      if (!r.reservation_date) return false;
      const created = new Date(r.reservation_date).getTime();
      fechaValida = created >= start && created <= end;
    } else if (soloSesionActual) {
      const today = new Date().toISOString().split('T')[0];
      fechaValida = r.reservation_date === today;
    } else if (startDate && endDate) {
      fechaValida = r.reservation_date >= startDate && r.reservation_date <= endDate;
    }
    if (!fechaValida) return false;

    // Filtrado por estado
    if (filter === 'Por Confirmar') return r.confirmed === false && r.cancelled === false && r.attended === false ;
    if (filter === 'Canceladas') return r.cancelled === true;
    if (filter === 'Confirmadas') return r.confirmed === true && r.cancelled === false && r.attended === false && r.approved === true && r.arrived === false;
    if (filter === 'En proceso') return r.attended === false && r.cancelled === false && r.confirmed === true && r.approved === true && r.arrived === true;
    return true;
  })
  .sort((a, b) => new Date(b.reservation_date).getTime() - new Date(a.reservation_date).getTime());

  const filterReservations = (
    <div className="filter-reservation-container">
      <Button className="filter-button" variant="none" onClick={() => setShowFilters(true)}>
        Filtros <FaFilter />
      </Button>

      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end" className="mp-modal-slider filter-list">
        <Offcanvas.Header>
          <div className="form-modal-title-container">
            <Offcanvas.Title className="form-modal-title">Opciones de filtrado</Offcanvas.Title>
          </div>
          <Button variant="none" onClick={() => setShowFilters(false)} className="modal-close-button">
            <FaTimes />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="receipt-displayer-filter-container">
            <DateFilter
              setStartDate={setStartDate}
              startDate={startDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
            <Form.Group className="mp-formgroup horizontal" controlId="soloSesionActual">
              <Form.Check
              type="checkbox"
              label="Solo sesión actual"
              checked={soloSesionActual}
              className="mediopunto-form-check-input"
              onChange={e => setSoloSesionActual(e.target.checked)}
              />
            </Form.Group>
          </div>
          {/* Botón de aplicar filtro eliminado */}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );

  if (loading) return <LoadingSpinner text="Obteniendo calendario" transparent />;
  if (reservations.length===0) return (
    <div className="no-data-available">
      <div className='no-data-list-reservation' style={{width:'100%'}}>
        {filterReservations}
        <h2 style={{ textAlign: 'center', marginTop: '2rem', color:'var(--text-color)', fontSize:'1.2rem' }}>¡No se han registrado reservaciones! Para visualizar anteriores utilice los filtros</h2>
      </div>
    </div>
  );

  return (
    <div className="reservation-stack">
      {filterReservations}
      <div className="reservation-menu">
        {['Canceladas','En proceso','Confirmadas','Por Confirmar'].map(item => (
          <Button
            key={item}
            variant="link"
            className={`reservation-header-menu-button mp-button light ${filter===item? 'active':''}`}
            onClick={()=>setFilter(item as any)}
          >
            {item}
          </Button>
        ))}
      </div>

      <div className="reservation-stack-list">
        {filtered.sort((a, b) => new Date(a.reservation_date).getTime() - new Date(b.reservation_date).getTime() || a.init - b.init).map(r => (
          <Card key={r.reservation_id} className="reservation-item">
        <Card.Header className="reservation-header">
          <div className="header-content">
        <span className="client-name">{r.client_name}</span>
        <span className="reservation-time">{timeFromInit(r.init)}</span>
          </div>
        </Card.Header>
        <Card.Body className="reservation-body">

        <div className="expanded-details">
          <p><FaUserFriends/> {r.n_people}</p>
          <p><FaPhone/> {r.phone_number}</p>
          <p>M{r.mesa_num}</p>
          {r.description && <p>Nota: {r.description}</p>}
            <p>Fecha {new Date(r.reservation_date + 'T00:00:00').toLocaleDateString('es-ES')}</p>
        </div>
        <p style={{ margin:'0%', color: new Date(r.reservation_date) < new Date(today) ? 'red' : undefined, fontWeight: new Date(r.reservation_date) < new Date(today) ? 'bold' : undefined }}>
          {new Date(r.reservation_date) < new Date(today) ? 'Reserva vencida' : ''}
        </p>
           
        </Card.Body>

        {(filter==='Por Confirmar' || filter==='Confirmadas' || filter==='En proceso') && (
          <Card.Footer className="reservation-footer">
    <div className="reservation-actions">
      {filter==='Por Confirmar' ? (
        <>
          <Button variant="none" size="sm" className="action-button" style={{gap:'0.3rem'}}
            onClick={()=>select(r,'cancel')}>Cancelar <FaBan/></Button>
          {r.approved
            ? <Button variant="none" size="sm" className="action-button" style={{gap:'0.3rem'}}
                onClick={()=>select(r,'confirm')}>Confirmar <FaCheck/></Button>
            : <Button variant="none" size="sm" className="action-button" style={{gap:'0.3rem'}}
                onClick={()=>select(r,'approved')}>Aprobar <FaCheck/></Button>
          }
          <Button variant="none" size="sm" className="action-button" style={{gap:'0.3rem'}}
            onClick={()=>select(r,'reject')}>Rechazar <FaTimes/></Button>
        </>
      ) : filter==='En proceso' ? (
        <>
          <Button variant="none" size="sm" className="action-button" style={{gap:'0.3rem'}}
            onClick={()=>select(r,'cancel')}>Cancelar <FaBan/></Button>
          <Button variant="none" size="sm" className="action-button" style={{gap:'0.3rem'}}
            onClick={()=>select(r,'attended')}>Atendida <FaCheck/></Button>
        </>
      ) : (
        <>
          <Button variant="none" size="sm" className="action-button" style={{gap:'0.3rem'}}
            onClick={()=>select(r,'cancel')}>Cancelar <FaBan/></Button>
          <Button variant="none" size="sm" className="action-button" style={{gap:'0.3rem'}}
            onClick={()=>select(r,'arrived')}>Recibir Clientes <FaUserFriends/></Button>
        </>
      )}
    </div>
          </Card.Footer>
        )}
          </Card>
        ))}
      </div>

      {currentModal==='cancel' && selectedReservation && (
        <RenderCancelModal
          reservation={selectedReservation}
          onHide={()=>setCurrentModal(null)}
          show
          onCancelConfirmed={() => handleCancelReservation(selectedReservation.reservation_id)}
        />
      )}
      {currentModal==='confirm' && selectedReservation && (
        <ConfirmReservationModal
          reservation={selectedReservation}
          onHide={()=>setCurrentModal(null)}
          show
          onConfirmConfirmed={() => handleConfirmReservation(selectedReservation.reservation_id)}
        />
      )}
      {currentModal==='approved'  && selectedReservation && <ApproveReservationModal reservation={selectedReservation} onHide={()=>setCurrentModal(null)} show />}
      {currentModal==='reject' && selectedReservation && (
        <RejectReservationModal
          reservation={selectedReservation}
          onHide={()=>setCurrentModal(null)}
          show
          onRejectConfirmed={() => handleRejectReservation(selectedReservation.reservation_id)}
        />
      )}
      {currentModal==='arrived'  && selectedReservation && (
        <MarkArrrivedModal
          reservation={selectedReservation}
          onHide={()=>setCurrentModal(null)}
          show
          onMarkAsArrived={() => handleMarkAsArrived(selectedReservation.reservation_id)}
        />
      )}
      {currentModal==='attended' && selectedReservation && (
        <MarkAttendedModal
          reservation={selectedReservation}
          onHide={()=>setCurrentModal(null)}
          show
          onMarkAsAttended={() => handleMarkAsAttended(selectedReservation.reservation_id)}
        />
      )}
    </div>
  );
};

export default ReservationStack;
